import { memo, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { BarChart3, MoreHorizontal, Network } from 'lucide-react';

import { IGraph, graphState } from '@chainlit/react-client';

import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { activeTargetIdState, graphSelectionState } from '@/state/graphSelection';
import {
  buildGraphId,
  groupGraphsByTarget,
  parseGraphId,
  type GraphView
} from '@/lib/graph-identifier';

const INLINE_PILL_LIMIT = 3;
const ALL_TARGETS_SENTINEL = '__all_targets__';

function getGraphIcon(graphId: string) {
  const parsed = parseGraphId(graphId);
  if (parsed?.view === 'Operation') return Network;
  if (parsed?.view === 'STIX') return BarChart3;
  return Network;
}

/**
 * Hierarchical graph picker. In single-target mode (or legacy identifiers
 * only) it degrades to the original inline pill row. When per-target graphs
 * are present (e.g. ``coa:target-1``, ``stix:target-1``), a target dropdown
 * appears and the pill row switches to view labels (COA / STIX / Operation)
 * scoped to the selected target. The underlying atom stays ``string`` so
 * existing consumers continue to work unchanged.
 */
const GraphTabSelector = memo(() => {
  const [selected, setSelected] = useRecoilState(graphSelectionState);
  const activeTargetId = useRecoilValue(activeTargetIdState);
  const graphs = useRecoilValue<Record<string, IGraph>>(graphState);
  // Touch sizing on mobile (the desktop chrome is a 24px tab strip); mirrors the
  // Terminal's actionButtons -> mobileActions switch so the controls stay tappable
  // when GraphTabSelector is mounted in the mobile graph header (#1125).
  const isMobile = useIsMobile();
  const pillCls = isMobile ? 'px-2.5 py-1.5 text-xs' : 'px-1.5 py-0.5 text-[10px]';
  const pillIcon = isMobile ? 'h-4 w-4' : 'h-3 w-3';

  const entries = useMemo(() => Object.entries(graphs), [graphs]);
  const allIds = useMemo(() => entries.map(([id]) => id), [entries]);
  const grouped = useMemo(() => groupGraphsByTarget(allIds), [allIds]);

  const targetIds = useMemo(() => Object.keys(grouped.targets).sort(), [grouped.targets]);
  const hasPerTarget = targetIds.length > 0;

  const parsedSelection = useMemo(() => parseGraphId(selected), [selected]);
  const currentTargetKey = useMemo(() => {
    if (!parsedSelection) return hasPerTarget ? targetIds[0] ?? ALL_TARGETS_SENTINEL : ALL_TARGETS_SENTINEL;
    if (parsedSelection.view === 'Operation') return ALL_TARGETS_SENTINEL;
    return parsedSelection.target_id ?? ALL_TARGETS_SENTINEL;
  }, [parsedSelection, targetIds, hasPerTarget]);

  const legacyStixId = useMemo(
    () => allIds.find((id) => id === 'STIX Overview' || id === 'STIX') ?? null,
    [allIds]
  );
  const allTargetsCoaId = useMemo(
    () => (allIds.includes('COA All Targets') ? 'COA All Targets' : null),
    [allIds]
  );
  const allTargetsStixId = useMemo(
    () => (allIds.includes('STIX All Targets') ? 'STIX All Targets' : null),
    [allIds]
  );

  if (entries.length === 0) return null;

  if (!hasPerTarget) {
    const primary = entries.slice(0, INLINE_PILL_LIMIT);
    const overflow = entries.slice(INLINE_PILL_LIMIT);
    return (
      <div className="flex items-center gap-0.5">
        {primary.map(([id, graph]) => {
          const Icon = getGraphIcon(id);
          const isSelected = selected === id;
          return (
            <button
              type="button"
              key={id}
              onClick={() => setSelected(id)}
              title={graph.name}
              className={cn(
                'inline-flex items-center gap-1 rounded',
                pillCls,
                'font-medium uppercase tracking-wide transition-colors',
                'border-b-2',
                isSelected
                  ? 'text-primary border-primary'
                  : 'text-muted-foreground border-transparent hover:text-foreground hover:bg-accent'
              )}
            >
              <Icon className={pillIcon} />
              <span className="whitespace-nowrap">{graph.name}</span>
            </button>
          );
        })}
        {overflow.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                title="More graphs"
                className="inline-flex items-center rounded px-1 py-0.5 text-muted-foreground hover:text-foreground hover:bg-accent border-b-2 border-transparent"
              >
                <MoreHorizontal className={pillIcon} />
                <span className="ml-0.5 text-[10px] font-medium">+{overflow.length}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              sideOffset={4}
              className="w-52 p-1 bg-popover text-popover-foreground border-border shadow-lg"
            >
              <div className="flex flex-col">
                {overflow.map(([id, graph]) => {
                  const Icon = getGraphIcon(id);
                  const isSelected = selected === id;
                  return (
                    <button
                      type="button"
                      key={id}
                      onClick={() => setSelected(id)}
                      className={cn(
                        'flex items-center gap-2 rounded px-2 py-1.5 text-xs',
                        isSelected
                          ? 'bg-primary/15 text-primary'
                          : 'text-foreground hover:bg-accent'
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      <span className="truncate">{graph.name}</span>
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    );
  }

  const views: GraphView[] =
    currentTargetKey === ALL_TARGETS_SENTINEL
      ? ['Operation', 'COA', 'STIX']
      : ['COA'];

  const resolveIdForView = (view: GraphView): string | null => {
    if (currentTargetKey === ALL_TARGETS_SENTINEL) {
      if (view === 'Operation') return grouped.operationIds[0] ?? null;
      if (view === 'COA') return allTargetsCoaId;
      if (view === 'STIX') return allTargetsStixId ?? legacyStixId;
    }
    if (view === 'COA') {
      const bucket = grouped.targets[currentTargetKey];
      return bucket?.coa ?? null;
    }
    return null;
  };

  const viewLabel = (view: GraphView): string =>
    view === 'Operation' ? 'Operations' : view;

  const onPickTarget = (next: string) => {
    if (next === ALL_TARGETS_SENTINEL) {
      const op = grouped.operationIds[0];
      if (op) setSelected(op);
      return;
    }
    const bucket = grouped.targets[next];
    if (!bucket) return;
    const resolved = bucket.coa ?? bucket.stix;
    if (resolved) setSelected(resolved);
    void buildGraphId;
  };

  const triggerLabel =
    currentTargetKey === ALL_TARGETS_SENTINEL ? 'All targets' : currentTargetKey;
  const triggerIsActive =
    currentTargetKey !== ALL_TARGETS_SENTINEL && currentTargetKey === activeTargetId;

  return (
    <div className="flex items-center gap-1">
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex items-center gap-1 rounded',
              pillCls,
              'font-medium uppercase tracking-wide border-b-2',
              'text-foreground border-muted-foreground/40 hover:bg-accent'
            )}
            title={
              activeTargetId
                ? `Target filter — active target: ${activeTargetId}`
                : 'Target filter'
            }
          >
            <span className="whitespace-nowrap">{triggerLabel}</span>
            {triggerIsActive && (
              <span className="ml-1 rounded bg-redteam/20 px-1 py-px text-[9px] font-semibold uppercase tracking-wider text-redteam">
                Active
              </span>
            )}
            <MoreHorizontal className={pillIcon} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="w-64 p-1 bg-popover text-popover-foreground border-border shadow-lg"
        >
          <div className="flex flex-col">
            {grouped.operationIds[0] && (
              <button
                type="button"
                onClick={() => onPickTarget(ALL_TARGETS_SENTINEL)}
                className={cn(
                  'flex items-center gap-2 rounded px-2 py-1.5 text-xs',
                  currentTargetKey === ALL_TARGETS_SENTINEL
                    ? 'bg-primary/15 text-primary'
                    : 'text-foreground hover:bg-accent'
                )}
              >
                <Network className="h-3.5 w-3.5" />
                <span className="truncate">All targets (Operations)</span>
                {activeTargetId && (
                  <span className="ml-auto text-[10px] text-muted-foreground">
                    active: {activeTargetId}
                  </span>
                )}
              </button>
            )}
            {targetIds.map((tid) => {
              const isAgentActive = tid === activeTargetId;
              return (
                <button
                  type="button"
                  key={tid}
                  onClick={() => onPickTarget(tid)}
                  className={cn(
                    'flex items-center gap-2 rounded px-2 py-1.5 text-xs',
                    currentTargetKey === tid
                      ? 'bg-primary/15 text-primary'
                      : 'text-foreground hover:bg-accent'
                  )}
                >
                  <Network className="h-3.5 w-3.5" />
                  <span className="truncate">{tid}</span>
                  {isAgentActive && (
                    <span className="ml-auto rounded bg-redteam/20 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wider text-redteam">
                      Active
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      <div className="flex items-center gap-0.5">
        {views.map((view) => {
          const id = resolveIdForView(view);
          const isSelected = id !== null && id === selected;
          const disabled = id === null;
          return (
            <button
              type="button"
              key={view}
              disabled={disabled}
              onClick={() => id && setSelected(id)}
              className={cn(
                'inline-flex items-center gap-1 rounded',
                pillCls,
                'font-medium uppercase tracking-wide transition-colors',
                'border-b-2',
                disabled && 'opacity-40 cursor-not-allowed',
                !disabled && isSelected
                  ? 'text-primary border-primary'
                  : 'text-muted-foreground border-transparent hover:text-foreground hover:bg-accent'
              )}
              title={viewLabel(view)}
            >
              {viewLabel(view)}
            </button>
          );
        })}
      </div>
    </div>
  );
});

GraphTabSelector.displayName = 'GraphTabSelector';

export default GraphTabSelector;
